import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Pagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Snackbar,
    Alert
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddCardIcon from '@mui/icons-material/AddCard';
import instance from '../../utils/axiosConfig';

function UserSearch({ userType }) { // userType can be 'distributors' or 'businesses'
    const [searchQuery, setSearchQuery] = useState('');
    const [status, setStatus] = useState('');
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [banDialogOpen, setBanDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [banLength, setBanLength] = useState('');
    const [banReason, setBanReason] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [subscriptionDialog, setSubscriptionDialog] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState({
        planId: '',
        months: 1
    });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await instance.get(`/api/moderator-management/search-${userType}`, {
                params: {
                    query: searchQuery,
                    status,
                    page,
                    limit: 10
                }
            });
            setUsers(response.data[userType]);
            setPagination(response.data.pagination);
        } catch (error) {
            showSnackbar(
                `Error al buscar ${userType === 'distributors' ? 'distribuidores' : 'negocios'}`,
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [page, searchQuery, status]);

    const handleBanClick = (user) => {
        setSelectedUser(user);
        setBanDialogOpen(true);
    };

    const handleBanSubmit = async () => {
        try {
            await instance.post(`/api/moderator-management/ban-${userType.slice(0, -1)}/${selectedUser._id}`, {
                banLength: parseInt(banLength),
                reason: banReason
            });
            showSnackbar('Usuario suspendido exitosamente');
            setBanDialogOpen(false);
            setBanLength('');
            setBanReason('');
            fetchUsers();
        } catch (error) {
            showSnackbar('Error al suspender usuario', 'error');
        }
    };

    const handleUnban = async (user) => {
        try {
            await instance.post(`/api/moderator-management/unban/${userType.slice(0, -1)}/${user._id}`);
            showSnackbar('Usuario desbloqueado exitosamente');
            fetchUsers();
        } catch (error) {
            showSnackbar('Error al desbloquear usuario', 'error');
        }
    };

    const handleAddSubscription = async () => {
        try {
            await instance.post(`/api/moderator-management/add-subscription/${selectedUser._id}`, subscriptionData);
            showSnackbar('Suscripción agregada exitosamente');
            setSubscriptionDialog(false);
            setSubscriptionData({ planId: '', months: 1 });
            fetchUsers();
        } catch (error) {
            showSnackbar('Error al agregar suscripción', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'verified': return 'success';
            case 'unverified': return 'warning';
            case 'banned': return 'error';
            case 'failed_verification': return 'error';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <TextField
                    label={`Buscar por nombre, ${userType === 'distributors' ? 'razón social' : 'razon social'}, CUIT o email`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flexGrow: 1 }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Estado</InputLabel>
                    <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        label="Estado"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {userType === 'distributors' ? (
                            <>
                                <MenuItem value="verified">Verificado</MenuItem>
                                <MenuItem value="unverified">Sin verificar</MenuItem>
                                <MenuItem value="failed_verification">Verificación fallida</MenuItem>
                            </>
                        ) : (
                            <MenuItem value="active">Activo</MenuItem>
                        )}
                        <MenuItem value="banned">Suspendido</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>{userType === 'distributors' ? 'Razón Social' : 'Razon Social'}</TableCell>
                            <TableCell>CUIT</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Estado</TableCell>
                            {userType === 'distributors' && <TableCell>Plan</TableCell>}
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user._id}>
                                <TableCell>{user.name}</TableCell>
                                <TableCell>{userType === 'distributors' ? user.governmentName : user.razonSocial}</TableCell>
                                <TableCell>{user.CUIT}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={user.status} 
                                        color={getStatusColor(user.status)}
                                        size="small"
                                    />
                                </TableCell>
                                {userType === 'distributors' && (
                                    <TableCell>
                                        {user.subscription?.planId || 'Sin plan'}
                                    </TableCell>
                                )}
                                <TableCell>
                                    {user.status === 'banned' ? (
                                        <IconButton
                                            onClick={() => handleUnban(user)}
                                            color="primary"
                                            title="Desbloquear usuario"
                                        >
                                            <LockOpenIcon />
                                        </IconButton>
                                    ) : (
                                        <IconButton 
                                            onClick={() => handleBanClick(user)}
                                            disabled={user.status === 'banned'}
                                            color="error"
                                            title="Suspender usuario"
                                        >
                                            <BlockIcon />
                                        </IconButton>
                                    )}
                                    {userType === 'distributors' && (
                                        <IconButton
                                            onClick={() => {
                                                setSelectedUser(user);
                                                setSubscriptionDialog(true);
                                            }}
                                            color="primary"
                                            title="Agregar suscripción"
                                        >
                                            <AddCardIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {users.length === 0 && !loading && (
                <Typography sx={{ mt: 2, textAlign: 'center' }}>
                    No se encontraron resultados
                </Typography>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                />
            </Box>

            <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)}>
                <DialogTitle>Suspender Usuario</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            ¿Estás seguro que deseas suspender a <strong>{selectedUser?.name}</strong>?
                        </Typography>
                        <TextField
                            label="Duración (días)"
                            type="number"
                            value={banLength}
                            onChange={(e) => setBanLength(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Razón"
                            multiline
                            rows={4}
                            value={banReason}
                            onChange={(e) => setBanReason(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBanDialogOpen(false)}>Cancelar</Button>
                    <Button 
                        onClick={handleBanSubmit}
                        variant="contained" 
                        color="error"
                        disabled={!banLength || !banReason}
                    >
                        Suspender
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={subscriptionDialog} onClose={() => setSubscriptionDialog(false)}>
                <DialogTitle>Agregar Suscripción</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Agregar suscripción para <strong>{selectedUser?.name}</strong>
                        </Typography>
                        <TextField
                            label="Plan ID"
                            value={subscriptionData.planId}
                            onChange={(e) => setSubscriptionData({
                                ...subscriptionData,
                                planId: e.target.value
                            })}
                            fullWidth
                        />
                        <TextField
                            label="Duración (meses)"
                            type="number"
                            value={subscriptionData.months}
                            onChange={(e) => setSubscriptionData({
                                ...subscriptionData,
                                months: e.target.value
                            })}
                            fullWidth
                            InputProps={{ inputProps: { min: 1 } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubscriptionDialog(false)}>Cancelar</Button>
                    <Button 
                        onClick={handleAddSubscription}
                        variant="contained" 
                        color="primary"
                        disabled={!subscriptionData.planId || !subscriptionData.months}
                    >
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default UserSearch; 